import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import saApi, { SA_API } from "../../superadminApi";
import { toast } from "sonner";
import { FONTS, FONT_OPTIONS } from "../../landingFonts";
import {
  ShieldCheck, LogOut, Building2, Users, CreditCard, Plus, UserPlus, Trash2,
  Pencil, X, CheckCircle2, Crown, Layers, BadgeCheck, Upload, Image as ImageIcon,
  PauseCircle, PlayCircle, Ban, History, Globe, Save,
  Inbox, ArrowUp, ArrowDown, Eye, EyeOff, Mail, RefreshCw, FileText, Wrench, Clock, Lock, ExternalLink, GripVertical, Heading,
} from "lucide-react";
import RichTextEditor from "../../components/RichTextEditor";
import PageSeoFields from "../../components/PageSeoFields";
import EsgIntegrationPanel from "../../components/EsgIntegrationPanel";
import ConsultantsPanel from "./ConsultantsPanel";

const PLAN_COLORS = { starter: "#0d6cb8", business: "#7c3aed", enterprise: "#ff7e00" };
const money = (cents, cur) => `${(cents / 100).toFixed(0)} ${(cur || "eur").toUpperCase()}`;
const logoUrl = (code) => `${SA_API}/companies/${code}/logo?t=${Date.now()}`;
const STATUS_LABEL = { active: "Attivo", suspended: "Sospeso", canceled: "Disdetto", none: "Nessun piano", pending: "In attesa" };

const Modal = ({ title, onClose, children, testid }) => (
  <>
    <div className="fixed inset-0 bg-black/60 z-[80]" onClick={onClose} />
    <div className="fixed inset-0 z-[81] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-[520px] max-h-[90vh] overflow-y-auto shadow-2xl" data-testid={testid} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 h-14 border-b border-gray-100">
          <h3 className="text-[16px] font-black text-[#16345c]">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="sa-modal-close"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  </>
);

const Field = ({ label, ...props }) => (
  <label className="block mb-3">
    <span className="block text-[12px] font-semibold text-gray-600 mb-1">{label}</span>
    <input {...props} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[14px] focus:border-[#16345c] outline-none" />
  </label>
);

const SuperadminDashboard = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [superadmins, setSuperadmins] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newCompany, setNewCompany] = useState(null);
  const [adminFor, setAdminFor] = useState(null);
  const [planFor, setPlanFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [newSa, setNewSa] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      saApi.get("/superadmin/companies"),
      saApi.get("/superadmin/superadmins"),
      saApi.get("/superadmin/plans"),
    ]).then(([c, s, p]) => {
      setCompanies(c.data.companies || []);
      setSuperadmins(s.data.superadmins || []);
      setPlans(p.data.plans || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    saApi.get("/superadmin/me").then((r) => setMe(r.data)).catch(() => navigate("/superadmin/login", { replace: true }));
    load();
  }, [load, navigate]);

  // Ritorno da Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      toast.info("Pagamento annullato");
      window.history.replaceState({}, "", "/superadmin/dashboard");
      return;
    }
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const r = await saApi.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success("Abbonamento attivato con successo!");
          window.history.replaceState({}, "", "/superadmin/dashboard");
          load();
          return;
        }
      } catch { /* keep polling */ }
      if (tries < 8) setTimeout(poll, 2000);
      else {
        toast.info("Pagamento in elaborazione, aggiorna tra poco.");
        window.history.replaceState({}, "", "/superadmin/dashboard");
      }
    };
    poll();
  }, [load]);

  const logout = async () => {
    try { await saApi.post("/superadmin/auth/logout"); } catch (e) { console.error("Logout superadmin non riuscito:", e); }
    navigate("/superadmin/login", { replace: true });
  };

  const managed = companies.filter((c) => !c.builtin);
  const totalEmployees = companies.reduce((s, c) => s + (c.employee_count || 0), 0);
  const activeSubs = managed.filter((c) => c.plan_status === "active").length;

  const doSubscribe = async (companyId, lookup_key) => {
    try {
      const r = await saApi.post(`/superadmin/companies/${companyId}/subscribe`, { lookup_key, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'avvio del pagamento");
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0b1220] flex items-center justify-center text-gray-400">Caricamento…</div>;

  return (
    <div className="min-h-screen bg-[#0b1220]" data-testid="superadmin-dashboard">
      {/* Header */}
      <div className="bg-[#111a2e] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl bg-[#ff7e00]/15 border border-[#ff7e00]/40 flex items-center justify-center"><ShieldCheck size={18} className="text-[#ff7e00]" /></span>
            <div>
              <div className="text-white font-black text-[15px] leading-none">Console Superadmin</div>
              <div className="text-gray-400 text-[11px] mt-0.5">{me?.email}</div>
            </div>
          </div>
          <button onClick={logout} data-testid="sa-logout-btn" className="flex items-center gap-2 text-gray-300 hover:text-white text-[13px] font-semibold border border-white/15 hover:border-white/40 px-3 py-1.5 rounded-lg transition-colors">
            <LogOut size={15} /> Esci
          </button>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { t: "Aziende attive", v: companies.filter((c) => c.active).length, icon: Building2, tid: "sa-stat-companies" },
            { t: "Dipendenti totali", v: totalEmployees, icon: Users, tid: "sa-stat-employees" },
            { t: "Tenant gestiti", v: managed.length, icon: Layers, tid: "sa-stat-tenants" },
            { t: "Abbonamenti attivi", v: activeSubs, icon: CreditCard, tid: "sa-stat-subscriptions" },
          ].map((s) => (
            <div key={s.tid} data-testid={s.tid} className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <s.icon size={20} className="text-[#ff7e00] mb-3" />
              <div className="text-white text-[28px] font-black leading-none">{s.v}</div>
              <div className="text-gray-400 text-[12px] mt-1">{s.t}</div>
            </div>
          ))}
        </div>

        {/* Companies */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white text-[18px] font-black">Aziende</h2>
          <button onClick={() => setNewCompany({ code: "", label: "", tagline: "", accent: "#0d6cb8" })} data-testid="sa-new-company-btn"
            className="flex items-center gap-2 bg-[#ff7e00] hover:bg-[#e86f00] text-white text-[13px] font-bold px-4 py-2 rounded-lg transition-colors">
            <Plus size={16} /> Nuova azienda
          </button>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden mb-10">
          <table className="w-full text-left">
            <thead className="bg-white/5 text-gray-400 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="px-5 py-3">Azienda</th>
                <th className="px-3 py-3">Dipendenti</th>
                <th className="px-3 py-3">Piano</th>
                <th className="px-3 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="text-[13px]">
              {companies.map((c) => {
                const plan = plans.find((p) => p.key === c.plan);
                return (
                  <tr key={c.code} data-testid={`sa-company-row-${c.code}`} className="border-t border-white/5">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-black text-[13px] overflow-hidden shrink-0" style={{ backgroundColor: c.accent || "#334155" }}>
                          {c.has_logo ? <img src={logoUrl(c.code)} alt="" className="w-full h-full object-contain bg-white" /> : (c.short || c.label || "?").charAt(0).toUpperCase()}
                        </span>
                        <div>
                          <div className="text-white font-bold flex items-center gap-2">
                            {c.label}
                            {c.builtin && <span className="text-[10px] font-bold bg-white/10 text-gray-300 px-1.5 py-0.5 rounded-full">Predefinita</span>}
                            {!c.active && <span className="text-[10px] font-bold bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded-full">Disattivata</span>}
                          </div>
                          <div className="text-gray-500 text-[11px]">{c.code}{c.tagline ? ` · ${c.tagline}` : ""}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3.5 text-gray-300" data-testid={`sa-seats-${c.code}`}>
                      {c.seat_limit == null ? (
                        <span>{c.employee_count} <span className="text-gray-500 text-[11px]">({c.admin_count} admin)</span></span>
                      ) : (() => {
                        const used = c.employee_count || 0, lim = c.seat_limit;
                        const pct = Math.min(100, Math.round((used / Math.max(1, lim)) * 100));
                        const full = used >= lim, near = !full && pct >= 80;
                        const col = full ? "bg-[#c00510]" : near ? "bg-amber-500" : "bg-[#009640]";
                        return (
                          <div className="min-w-[120px]">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`font-bold ${full ? "text-red-300" : "text-white"}`}>{used}/{lim}</span>
                              <span className="text-gray-500 text-[11px]">{c.admin_count} admin</span>
                            </div>
                            <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${col}`} style={{ width: `${pct}%` }} data-testid={`sa-seats-fill-${c.code}`} />
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3.5">
                      {c.builtin ? <span className="text-gray-500">—</span> : c.plan_status === "active" ? (
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-full text-white" style={{ backgroundColor: PLAN_COLORS[c.plan] || "#334155" }}>
                          <BadgeCheck size={12} /> {plan?.name || c.plan}
                        </span>
                      ) : (c.plan_status === "suspended" || c.plan_status === "canceled") ? (
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-full bg-red-500/20 text-red-300">
                          {c.plan_status === "suspended" ? "Sospeso" : "Disdetto"}
                        </span>
                      ) : <span className="text-gray-500 text-[12px]">Nessun piano</span>}
                    </td>
                    <td className="px-3 py-3.5">
                      {c.builtin ? <span className="text-gray-600 text-[12px] block text-right">Sola lettura</span> : (
                        <div className="flex items-center justify-end gap-1.5">
                          <button title="Crea admin" onClick={() => setAdminFor(c)} data-testid={`sa-create-admin-${c.id}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10"><UserPlus size={16} /></button>
                          <button title="Gestisci piano" onClick={() => setPlanFor(c)} data-testid={`sa-manage-plan-${c.id}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10"><CreditCard size={16} /></button>
                          <button title="Modifica" onClick={() => setEditFor({ ...c })} data-testid={`sa-edit-company-${c.id}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10"><Pencil size={16} /></button>
                          {c.active && <button title="Disattiva" onClick={() => { if (window.confirm(`Disattivare ${c.label}?`)) saApi.delete(`/superadmin/companies/${c.id}`).then(() => { toast.success("Azienda disattivata"); load(); }); }} data-testid={`sa-deactivate-company-${c.id}`} className="p-1.5 rounded-lg text-red-300 hover:text-red-200 hover:bg-red-500/10"><Trash2 size={16} /></button>}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Superadmins */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white text-[18px] font-black flex items-center gap-2"><Crown size={18} className="text-[#ff7e00]" /> Superadmin</h2>
          <button onClick={() => setNewSa({ email: "", name: "", password: "" })} data-testid="sa-new-superadmin-btn"
            className="flex items-center gap-2 border border-white/15 hover:border-white/40 text-gray-200 text-[13px] font-bold px-4 py-2 rounded-lg transition-colors">
            <Plus size={16} /> Nuovo superadmin
          </button>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-2 mb-10" data-testid="sa-superadmins-list">
          {superadmins.map((s) => (
            <div key={s.id} className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-white/5">
              <span className="w-8 h-8 rounded-lg bg-[#ff7e00]/15 flex items-center justify-center text-[#ff7e00]"><Crown size={15} /></span>
              <div className="flex-1"><div className="text-white text-[13px] font-bold">{s.name}</div><div className="text-gray-500 text-[11px]">{s.email}</div></div>
            </div>
          ))}
        </div>

        {/* Consulenti del lavoro + chiavi API Due Diligence */}
        <ConsultantsPanel companies={companies} />

        {/* Integrazione ESG */}
        <EsgIntegrationPanel />

        {/* Log consegne email */}
        <EmailLogsPanel />

        {/* Richieste di preventivo */}
        <QuoteRequests />

        {/* Modalità Manutenzione */}
        <MaintenanceEditor />

        {/* Contenuti Sito (Landing) */}
        <LandingEditor />

        {/* Privacy & Cookie Policy */}
        <LegalEditor />

        {/* Pagine personalizzate */}
        <CustomPagesEditor />

        {/* Pagine del sito (sezioni landing) */}
        <SitePagesEditor />
      </div>
      {newCompany && (
        <Modal title="Nuova azienda" testid="sa-company-modal" onClose={() => setNewCompany(null)}>
          <Field label="Codice (identificativo, senza spazi)" value={newCompany.code} data-testid="sa-company-code" onChange={(e) => setNewCompany({ ...newCompany, code: e.target.value })} placeholder="es. acme" />
          <Field label="Nome azienda" value={newCompany.label} data-testid="sa-company-label" onChange={(e) => setNewCompany({ ...newCompany, label: e.target.value })} placeholder="es. Acme S.p.A." />
          <Field label="Slogan (opzionale)" value={newCompany.tagline} data-testid="sa-company-tagline" onChange={(e) => setNewCompany({ ...newCompany, tagline: e.target.value })} />
          <label className="block mb-4"><span className="block text-[12px] font-semibold text-gray-600 mb-1">Colore</span>
            <input type="color" value={newCompany.accent} data-testid="sa-company-accent" onChange={(e) => setNewCompany({ ...newCompany, accent: e.target.value })} className="w-16 h-9 rounded border border-gray-200" /></label>
          <button data-testid="sa-company-save" onClick={async () => {
            try { await saApi.post("/superadmin/companies", newCompany); toast.success("Azienda creata"); setNewCompany(null); load(); }
            catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
          }} className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold py-2.5 rounded-lg text-[14px]">Crea azienda</button>
        </Modal>
      )}

      {/* Edit company modal */}
      {editFor && (
        <Modal title={`Modifica ${editFor.label}`} testid="sa-edit-modal" onClose={() => setEditFor(null)}>
          <Field label="Nome azienda" value={editFor.label} onChange={(e) => setEditFor({ ...editFor, label: e.target.value })} />
          <Field label="Slogan" value={editFor.tagline || ""} onChange={(e) => setEditFor({ ...editFor, tagline: e.target.value })} />
          <label className="block mb-4"><span className="block text-[12px] font-semibold text-gray-600 mb-1">Colore aziendale</span>
            <input type="color" value={editFor.accent || "#0d6cb8"} data-testid="sa-edit-accent" onChange={(e) => setEditFor({ ...editFor, accent: e.target.value })} className="w-16 h-9 rounded border border-gray-200" /></label>

          <div className="mb-4">
            <span className="block text-[12px] font-semibold text-gray-600 mb-2">Logo azienda (login e PDF)</span>
            <div className="flex items-center gap-3">
              <span className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                {editFor.has_logo ? <img src={logoUrl(editFor.code)} alt="logo" className="w-full h-full object-contain" data-testid="sa-edit-logo-preview" /> : <ImageIcon size={22} className="text-gray-300" />}
              </span>
              <label className="flex items-center gap-2 cursor-pointer bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-3 py-2 rounded-lg">
                <Upload size={15} /> {editFor.has_logo ? "Sostituisci" : "Carica logo"}
                <input type="file" accept="image/png,image/jpeg" className="hidden" data-testid="sa-edit-logo-input" onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const fd = new FormData(); fd.append("file", f);
                  try { await saApi.post(`/superadmin/companies/${editFor.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Logo caricato"); setEditFor({ ...editFor, has_logo: true }); load(); }
                  catch (err) { toast.error(err?.response?.data?.detail || "Errore logo"); }
                }} />
              </label>
              {editFor.has_logo && <button data-testid="sa-edit-logo-remove" onClick={async () => { await saApi.delete(`/superadmin/companies/${editFor.id}/logo`); toast.success("Logo rimosso"); setEditFor({ ...editFor, has_logo: false }); load(); }} className="text-[#c00510] border border-red-300 hover:bg-red-50 text-[13px] font-semibold px-3 py-2 rounded-lg">Rimuovi</button>}
            </div>
          </div>

          <button data-testid="sa-edit-save" onClick={async () => {
            try { await saApi.put(`/superadmin/companies/${editFor.id}`, { label: editFor.label, tagline: editFor.tagline, accent: editFor.accent }); toast.success("Salvato"); setEditFor(null); load(); }
            catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
          }} className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold py-2.5 rounded-lg text-[14px]">Salva</button>
        </Modal>
      )}

      {/* Create admin modal */}
      {adminFor && (
        <Modal title={`Nuovo admin · ${adminFor.label}`} testid="sa-admin-modal" onClose={() => setAdminFor(null)}>
          <p className="text-[12px] text-gray-500 mb-4">L'admin accede dal login normale scegliendo l'azienda <b>{adminFor.label}</b>.</p>
          <AdminForm company={adminFor} onDone={() => { setAdminFor(null); load(); }} />
        </Modal>
      )}

      {/* Plan modal */}
      {planFor && (
        <Modal title={`Abbonamento · ${planFor.label}`} testid="sa-plan-modal" onClose={() => setPlanFor(null)}>
          <PlanManager company={planFor} plans={plans} onSubscribe={doSubscribe} onChanged={() => { setPlanFor(null); load(); }} />
        </Modal>
      )}

      {/* New superadmin modal */}
      {newSa && (
        <Modal title="Nuovo superadmin" testid="sa-superadmin-modal" onClose={() => setNewSa(null)}>
          <Field label="Nome" value={newSa.name} data-testid="sa-sa-name" onChange={(e) => setNewSa({ ...newSa, name: e.target.value })} />
          <Field label="Email" type="email" value={newSa.email} data-testid="sa-sa-email" onChange={(e) => setNewSa({ ...newSa, email: e.target.value })} />
          <Field label="Password" type="password" value={newSa.password} data-testid="sa-sa-password" onChange={(e) => setNewSa({ ...newSa, password: e.target.value })} />
          <button data-testid="sa-sa-save" onClick={async () => {
            try { await saApi.post("/superadmin/superadmins", newSa); toast.success("Superadmin creato"); setNewSa(null); load(); }
            catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
          }} className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold py-2.5 rounded-lg text-[14px]">Crea superadmin</button>
        </Modal>
      )}
    </div>
  );
};

// Gestione piano: stato corrente, sospendi/riattiva/disdici, storico pagamenti, scelta piano
const PlanManager = ({ company, plans, onSubscribe, onChanged }) => {
  const [payments, setPayments] = useState([]);
  const status = company.plan_status;
  const currentPlan = plans.find((p) => p.key === company.plan);

  useEffect(() => {
    saApi.get(`/superadmin/companies/${company.id}/payments`).then((r) => setPayments(r.data.payments || [])).catch(() => {});
  }, [company.id]);

  const act = async (path, label) => {
    try { await saApi.post(`/superadmin/companies/${company.id}/plan/${path}`); toast.success(label); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div>
      {/* Stato corrente */}
      <div className="rounded-xl border border-gray-200 p-4 mb-4" data-testid="sa-plan-current">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-400 uppercase tracking-wide font-bold">Stato abbonamento</div>
            <div className="text-[16px] font-black text-[#16345c]">{currentPlan ? currentPlan.name : "Nessun piano"}
              <span className={`ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${status === "active" ? "bg-emerald-100 text-emerald-700" : status === "suspended" ? "bg-amber-100 text-amber-700" : status === "canceled" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-500"}`}>{STATUS_LABEL[status] || "Nessun piano"}</span>
            </div>
          </div>
          <div className="flex gap-2">
            {status === "active" && <button data-testid="sa-plan-suspend" onClick={() => act("suspend", "Abbonamento sospeso")} className="flex items-center gap-1 text-[12px] font-bold text-amber-700 border border-amber-300 hover:bg-amber-50 px-2.5 py-1.5 rounded-lg"><PauseCircle size={15} /> Sospendi</button>}
            {status === "suspended" && <button data-testid="sa-plan-resume" onClick={() => act("resume", "Abbonamento riattivato")} className="flex items-center gap-1 text-[12px] font-bold text-emerald-700 border border-emerald-300 hover:bg-emerald-50 px-2.5 py-1.5 rounded-lg"><PlayCircle size={15} /> Riattiva</button>}
            {(status === "active" || status === "suspended") && <button data-testid="sa-plan-cancel" onClick={() => { if (window.confirm("Disdire l'abbonamento? Verrà annullato anche su Stripe.")) act("cancel", "Abbonamento disdetto"); }} className="flex items-center gap-1 text-[12px] font-bold text-[#c00510] border border-red-300 hover:bg-red-50 px-2.5 py-1.5 rounded-lg"><Ban size={15} /> Disdici</button>}
          </div>
        </div>
      </div>

      {/* Scelta / cambio piano */}
      <div className="space-y-3 mb-5">
        {plans.map((p) => {
          const current = company.plan === p.key && status === "active";
          return (
            <div key={p.key} data-testid={`sa-plan-${p.key}`} className="border border-gray-200 rounded-xl p-4" style={{ borderColor: current ? PLAN_COLORS[p.key] : undefined }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-black" style={{ color: PLAN_COLORS[p.key] }}>{p.name}</span>
                  {current && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full"><CheckCircle2 size={11} /> Attivo</span>}
                </div>
                <span className="text-[15px] font-black text-[#16345c]">{money(p.amount, p.currency)}<span className="text-[11px] text-gray-400 font-semibold">/mese</span></span>
              </div>
              <ul className="text-[12px] text-gray-500 mb-3 space-y-0.5">{p.features.map((f) => <li key={f}>• {f}</li>)}</ul>
              <button data-testid={`sa-plan-subscribe-${p.key}`} onClick={() => onSubscribe(company.id, p.lookup_key)}
                className="w-full text-white font-bold py-2 rounded-lg text-[13px] transition-opacity hover:opacity-90" style={{ backgroundColor: PLAN_COLORS[p.key] }}>
                {current ? "Rinnova / cambia" : "Attiva con pagamento"}
              </button>
            </div>
          );
        })}
      </div>

      {/* Storico pagamenti */}
      <div data-testid="sa-plan-history">
        <div className="flex items-center gap-2 text-[12px] font-bold text-gray-600 mb-2"><History size={14} /> Storico pagamenti</div>
        {payments.length === 0 ? <p className="text-[12px] text-gray-400">Nessun pagamento registrato.</p> : (
          <div className="space-y-1.5">
            {payments.map((p, i) => (
              <div key={p.id || `${p.created_at || "pay"}-${i}`} className="flex items-center justify-between text-[12px] border border-gray-100 rounded-lg px-3 py-2">
                <span className="text-gray-600">{p.created_at ? new Date(p.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"} · <b className="text-[#16345c] capitalize">{p.plan || "—"}</b></span>
                <span className="flex items-center gap-2">
                  <span className="text-gray-500">{money(p.amount || 0, p.currency)}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${p.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{p.payment_status === "paid" ? "Pagato" : (p.payment_status || "in sospeso")}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Form separato per la creazione admin (stato locale isolato)
const AdminForm = ({ company, onDone }) => {
  const [f, setF] = useState({ matricola: "", email: "", name: "", password: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div>
      <Field label="Matricola / username" value={f.matricola} data-testid="sa-admin-matricola-input" onChange={set("matricola")} placeholder="es. acmeadmin" />
      <Field label="Nome completo" value={f.name} data-testid="sa-admin-name" onChange={set("name")} />
      <Field label="Email" type="email" value={f.email} data-testid="sa-admin-email" onChange={set("email")} />
      <Field label="Password iniziale" type="text" value={f.password} data-testid="sa-admin-password" onChange={set("password")} />
      <p className="text-[11px] text-gray-500 mb-2">Le credenziali (email + password) verranno inviate automaticamente via email al nuovo admin.</p>
      <button data-testid="sa-admin-save" onClick={async () => {
        try { await saApi.post(`/superadmin/companies/${company.id}/admin`, { ...f, origin_url: window.location.origin }); toast.success("Admin creato — email con le credenziali inviata"); onDone(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
      }} className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold py-2.5 rounded-lg text-[14px] mt-1">Crea admin</button>
    </div>
  );
};

const QuoteRequests = () => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    saApi.get("/superadmin/quote-requests").then((r) => setItems(r.data.requests || [])).catch((e) => { console.error("Caricamento preventivi non riuscito:", e); setItems([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await saApi.patch(`/superadmin/quote-requests/${id}`, { status }); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    try { await saApi.delete(`/superadmin/quote-requests/${id}`); toast.success("Richiesta eliminata"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  if (items === null) return null;
  const pending = items.filter((q) => q.status !== "handled").length;
  const fmtDate = (s) => (s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

  return (
    <div className="mb-10" data-testid="sa-quote-requests">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white text-[18px] font-black flex items-center gap-2">
          <Inbox size={18} className="text-[#ff7e00]" /> Richieste di preventivo
          {pending > 0 && <span className="text-[11px] font-bold bg-[#ff7e00] text-white px-2 py-0.5 rounded-full" data-testid="sa-quote-pending-count">{pending} nuove</span>}
        </h2>
        <button onClick={load} data-testid="sa-quote-refresh" className="flex items-center gap-2 text-gray-300 hover:text-white text-[13px] font-semibold border border-white/15 hover:border-white/40 px-3 py-2 rounded-lg transition-colors">
          <RefreshCw size={15} /> Aggiorna
        </button>
      </div>
      {items.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center text-gray-400 text-[14px]" data-testid="sa-quote-empty">
          Nessuna richiesta di preventivo ricevuta.
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[720px]">
              <thead className="bg-white/5 text-gray-400 text-[11px] uppercase tracking-wide">
                <tr>
                  <th className="px-5 py-3">Richiedente</th>
                  <th className="px-3 py-3">Azienda</th>
                  <th className="px-3 py-3">Messaggio</th>
                  <th className="px-3 py-3">Data</th>
                  <th className="px-3 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="text-[13px]">
                {items.map((q) => (
                  <tr key={q.id} data-testid={`sa-quote-row-${q.id}`} className={`border-t border-white/5 ${q.status !== "handled" ? "" : "opacity-60"}`}>
                    <td className="px-5 py-3.5">
                      <div className="text-white font-bold flex items-center gap-2">
                        {q.name}
                        {q.status !== "handled" && <span className="text-[10px] font-bold bg-[#ff7e00]/20 text-[#ffb066] px-1.5 py-0.5 rounded-full">Nuova</span>}
                      </div>
                      <a href={`mailto:${q.email}`} className="text-gray-400 text-[11px] hover:text-[#ff7e00] inline-flex items-center gap-1"><Mail size={11} /> {q.email}</a>
                    </td>
                    <td className="px-3 py-3.5 text-gray-300">{q.company}</td>
                    <td className="px-3 py-3.5 text-gray-400 max-w-[280px]"><div className="line-clamp-3 whitespace-pre-line" title={q.message}>{q.message || "—"}</div></td>
                    <td className="px-3 py-3.5 text-gray-400 text-[12px] whitespace-nowrap">{fmtDate(q.created_at)}</td>
                    <td className="px-3 py-3.5">
                      <div className="flex items-center justify-end gap-1.5">
                        {q.status !== "handled" ? (
                          <button title="Segna come gestita" onClick={() => setStatus(q.id, "handled")} data-testid={`sa-quote-handle-${q.id}`} className="p-1.5 rounded-lg text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10"><CheckCircle2 size={16} /></button>
                        ) : (
                          <button title="Segna come nuova" onClick={() => setStatus(q.id, "new")} data-testid={`sa-quote-reopen-${q.id}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10"><RefreshCw size={16} /></button>
                        )}
                        <button title="Elimina" onClick={() => remove(q.id)} data-testid={`sa-quote-delete-${q.id}`} className="p-1.5 rounded-lg text-red-300 hover:text-red-200 hover:bg-red-500/10"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const EmailLogsPanel = () => {
  const [data, setData] = useState({ logs: [], counts: {} });
  const [filter, setFilter] = useState("all");
  const load = useCallback(() => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    saApi.get(`/superadmin/emails${q}`).then((r) => setData(r.data || { logs: [], counts: {} })).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  const c = data.counts || {};
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
  return (
    <div className="mb-10" data-testid="sa-email-logs">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-white text-[18px] font-black flex items-center gap-2"><Mail size={18} className="text-[#ff7e00]" /> Log consegne email</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {[["all", "Tutte", c.total], ["sent", "Inviate", c.sent], ["failed", "Fallite", c.failed]].map(([k, l, n]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`sa-email-filter-${k}`}
              className={`text-[12px] font-bold px-3 py-1.5 rounded-lg border ${filter === k ? "bg-[#ff7e00] text-white border-[#ff7e00]" : "border-white/15 text-gray-300 hover:border-white/40"}`}>
              {l}{n != null ? ` (${n})` : ""}
            </button>
          ))}
          <button onClick={load} data-testid="sa-email-refresh" className="flex items-center gap-2 text-gray-300 hover:text-white text-[13px] font-semibold border border-white/15 hover:border-white/40 px-3 py-1.5 rounded-lg transition-colors">
            <RefreshCw size={15} /> Aggiorna
          </button>
        </div>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        {(!data.logs || data.logs.length === 0) ? (
          <div className="p-8 text-center text-gray-400 text-[14px]" data-testid="sa-email-empty">Nessuna email registrata.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[720px]">
              <thead className="bg-white/5 text-gray-400 text-[11px] uppercase tracking-wide">
                <tr>
                  <th className="px-5 py-3">Data</th>
                  <th className="px-3 py-3">Destinatario</th>
                  <th className="px-3 py-3">Oggetto</th>
                  <th className="px-3 py-3">Esito</th>
                  <th className="px-3 py-3">ID Resend</th>
                </tr>
              </thead>
              <tbody className="text-[13px]">
                {data.logs.map((l, i) => (
                  <tr key={l.id || i} data-testid={`sa-email-row-${i}`} className="border-t border-white/5">
                    <td className="px-5 py-3 text-gray-400 whitespace-nowrap">{fmt(l.created_at)}</td>
                    <td className="px-3 py-3 text-gray-200">{l.to}</td>
                    <td className="px-3 py-3 text-gray-300 max-w-[280px]">
                      <div className="truncate" title={l.subject}>{l.subject}</div>
                      {l.error && <div className="text-red-400 text-[11px] truncate" title={l.error}>{l.error}</div>}
                    </td>
                    <td className="px-3 py-3">
                      {l.status === "sent"
                        ? <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"><CheckCircle2 size={11} /> Inviata</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30"><X size={11} /> Fallita</span>}
                    </td>
                    <td className="px-3 py-3 text-gray-500 font-mono text-[11px]">{l.resend_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const LE_SECTIONS = [
  { key: "home", label: "Home (hero)", fields: ["title", "subtitle"] },
  { key: "chisiamo", label: "Chi siamo", fields: ["title", "body", "image"] },
  { key: "lavora", label: "Lavora con noi", fields: ["title", "body", "image"] },
  { key: "partners", label: "Partners", fields: ["title", "body", "image", "partners"] },
  { key: "contatti", label: "Contatti", fields: ["title", "body", "image", "email", "phone", "address"] },
];
const LE_BASE = process.env.REACT_APP_BACKEND_URL || "";
const LE_LABELS = { chisiamo: "Chi siamo", lavora: "Lavora con noi", partners: "Partners", contatti: "Contatti" };

const IMG_POS = [{ v: "left", l: "Sinistra" }, { v: "right", l: "Destra" }];
const IMG_FIT = [{ v: "contain", l: "Intera (senza tagliare)" }, { v: "cover", l: "Riempi (ritaglia)" }];
const IMG_RATIO = [{ v: "16:9", l: "16:9 (widescreen)" }, { v: "4:3", l: "4:3" }, { v: "1:1", l: "Quadrata" }, { v: "auto", l: "Originale" }];
const leInput = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[13px] text-white outline-none focus:border-[#ff7e00] placeholder:text-gray-500";

const LandingEditor = () => {
  const [sections, setSections] = useState(null);
  const [layout, setLayout] = useState([]);
  const [font, setFont] = useState("inter");
  const [fontHeading, setFontHeading] = useState("inter");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    saApi.get("/superadmin/landing-content").then((r) => { setSections(r.data.sections); setLayout(r.data.layout || []); setFont(r.data.font || "inter"); setFontHeading(r.data.font_heading || r.data.font || "inter"); }).catch((e) => console.error("Caricamento contenuti landing non riuscito:", e));
  }, []);

  const upd = (key, field, value) => setSections((s) => ({ ...s, [key]: { ...s[key], [field]: value } }));

  const moveSection = (idx, dir) => setLayout((l) => {
    const j = idx + dir;
    if (j < 0 || j >= l.length) return l;
    const next = [...l];
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const toggleVisible = (idx) => setLayout((l) => l.map((x, i) => (i === idx ? { ...x, visible: !x.visible } : x)));

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await saApi.post("/superadmin/landing/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const save = async () => {
    setSaving(true);
    try { await saApi.put("/superadmin/landing-content", { sections, layout, font, font_heading: fontHeading }); toast.success("Contenuti del sito aggiornati"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const preview = () => {
    try {
      localStorage.setItem("jobtimehr_landing_preview", JSON.stringify({ sections, layout, font, font_heading: fontHeading }));
      window.open("/?preview=1", "_blank", "noopener");
    } catch (e) { toast.error("Impossibile aprire l'anteprima"); }
  };

  if (!sections) return null;

  return (
    <div className="mt-2 mb-10" data-testid="sa-landing-editor">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white text-[18px] font-black flex items-center gap-2"><Globe size={18} className="text-[#ff7e00]" /> Contenuti Sito (Landing)</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setOpen((v) => !v)} className="text-gray-300 hover:text-white text-[13px] font-semibold border border-white/15 hover:border-white/40 px-3 py-2 rounded-lg transition-colors">
            {open ? "Comprimi" : "Espandi"}
          </button>
          <button onClick={preview} data-testid="sa-landing-preview" className="flex items-center gap-2 text-white text-[13px] font-semibold border border-white/25 hover:border-[#ff7e00] hover:text-[#ff7e00] px-3 py-2 rounded-lg transition-colors">
            <Eye size={15} /> Anteprima
          </button>
          <button onClick={save} disabled={saving} data-testid="sa-landing-save"
            className="flex items-center gap-2 bg-[#ff7e00] hover:bg-[#e56f00] disabled:opacity-60 text-white text-[13px] font-bold px-4 py-2 rounded-lg transition-colors">
            {saving ? <Loader /> : <Save size={16} />} Salva contenuti
          </button>
        </div>
      </div>
      {open && (
        <div className="space-y-4">
          {/* Font del sito pubblico */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="sa-landing-font">
            <div className="text-[#ffb066] text-[12px] font-black uppercase tracking-widest mb-1">Font del sito</div>
            <p className="text-[12px] text-gray-400 mb-3">Scegli i caratteri applicati alla landing: uno per i titoli e uno per il corpo del testo.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-[12px] font-semibold text-gray-400 mb-1">Font titoli</span>
                <select value={fontHeading} onChange={(e) => setFontHeading(e.target.value)} data-testid="sa-landing-font-heading-select"
                  className={leInput} style={{ fontFamily: (FONTS[fontHeading] || {}).stack }}>
                  {FONT_OPTIONS.map((k) => <option key={k} value={k} style={{ color: "#111" }}>{FONTS[k].label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-[12px] font-semibold text-gray-400 mb-1">Font testo</span>
                <select value={font} onChange={(e) => setFont(e.target.value)} data-testid="sa-landing-font-select"
                  className={leInput} style={{ fontFamily: (FONTS[font] || {}).stack }}>
                  {FONT_OPTIONS.map((k) => <option key={k} value={k} style={{ color: "#111" }}>{FONTS[k].label}</option>)}
                </select>
              </label>
            </div>
            <div className="mt-3 rounded-lg bg-white/[0.03] border border-white/10 px-4 py-3">
              <div className="text-white text-[22px] font-black" style={{ fontFamily: (FONTS[fontHeading] || {}).stack }}>Titolo di esempio</div>
              <div className="text-gray-300 text-[13px] mt-1" style={{ fontFamily: (FONTS[font] || {}).stack }}>Anteprima del corpo del testo: la piattaforma HR cucita su misura per te.</div>
            </div>
          </div>
          {/* Struttura: ordine e visibilità delle sezioni */}
          {layout.length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="sa-landing-layout">
              <div className="text-[#ffb066] text-[12px] font-black uppercase tracking-widest mb-1">Struttura e ordine sezioni</div>
              <p className="text-[12px] text-gray-400 mb-3">Riordina con le frecce e nascondi/mostra le sezioni sul sito. La Home resta sempre in cima.</p>
              <div className="space-y-2">
                {layout.map((item, i) => {
                  const label = LE_LABELS[item.key] || item.key;
                  return (
                    <div key={item.key} data-testid={`sa-layout-row-${item.key}`}
                      className={`flex items-center gap-3 rounded-lg border p-2.5 ${item.visible ? "bg-white/5 border-white/10" : "bg-white/[0.02] border-white/5 opacity-70"}`}>
                      <span className="text-gray-500 text-[12px] w-5 text-center">{i + 1}</span>
                      <span className="flex-1 text-white text-[13px] font-semibold">{label}{!item.visible && <span className="ml-2 text-[10px] font-bold text-gray-400">(nascosta)</span>}</span>
                      <button onClick={() => moveSection(i, -1)} disabled={i === 0} data-testid={`sa-layout-up-${item.key}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 disabled:opacity-30"><ArrowUp size={15} /></button>
                      <button onClick={() => moveSection(i, 1)} disabled={i === layout.length - 1} data-testid={`sa-layout-down-${item.key}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 disabled:opacity-30"><ArrowDown size={15} /></button>
                      <button onClick={() => toggleVisible(i)} data-testid={`sa-layout-toggle-${item.key}`}
                        className={`p-1.5 rounded-lg ${item.visible ? "text-emerald-300 hover:bg-emerald-500/10" : "text-gray-400 hover:bg-white/10"}`} title={item.visible ? "Nascondi" : "Mostra"}>
                        {item.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {LE_SECTIONS.map((sec) => {
            const s = sections[sec.key] || {};
            return (
              <div key={sec.key} className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid={`sa-landing-section-${sec.key}`}>
                <div className="text-[#ffb066] text-[12px] font-black uppercase tracking-widest mb-3">{sec.label}</div>
                <div className="space-y-3">
                  {sec.fields.includes("title") && <LEField label="Titolo" value={s.title} onChange={(v) => upd(sec.key, "title", v)} testid={`sa-landing-${sec.key}-title`} />}
                  {sec.fields.includes("subtitle") && <LEField label="Sottotitolo" area value={s.subtitle} onChange={(v) => upd(sec.key, "subtitle", v)} testid={`sa-landing-${sec.key}-subtitle`} />}
                  {sec.fields.includes("body") && <LEField label="Testo" area value={s.body} onChange={(v) => upd(sec.key, "body", v)} testid={`sa-landing-${sec.key}-body`} />}
                  {sec.fields.includes("email") && <LEField label="Email" value={s.email} onChange={(v) => upd(sec.key, "email", v)} testid={`sa-landing-${sec.key}-email`} />}
                  {sec.fields.includes("phone") && <LEField label="Telefono" value={s.phone} onChange={(v) => upd(sec.key, "phone", v)} testid={`sa-landing-${sec.key}-phone`} />}
                  {sec.fields.includes("address") && <LEField label="Indirizzo" value={s.address} onChange={(v) => upd(sec.key, "address", v)} testid={`sa-landing-${sec.key}-address`} />}
                  {sec.fields.includes("image") && (
                    <>
                      <LEImage label="Immagine" url={s.image_url} onUpload={async (f) => upd(sec.key, "image_url", await uploadImage(f))} onClear={() => upd(sec.key, "image_url", "")} testid={`sa-landing-${sec.key}-image`} />
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid={`sa-landing-${sec.key}-imglayout`}>
                        <LESelect label="Posizione immagine" value={s.image_position || "right"} options={IMG_POS} onChange={(v) => upd(sec.key, "image_position", v)} testid={`sa-landing-${sec.key}-imgpos`} />
                        <LESelect label="Adattamento" value={s.image_fit || "contain"} options={IMG_FIT} onChange={(v) => upd(sec.key, "image_fit", v)} testid={`sa-landing-${sec.key}-imgfit`} />
                        <LESelect label="Proporzioni" value={s.image_ratio || "16:9"} options={IMG_RATIO} onChange={(v) => upd(sec.key, "image_ratio", v)} testid={`sa-landing-${sec.key}-imgratio`} />
                      </div>
                    </>
                  )}
                  {sec.fields.includes("partners") && (
                    <LEPartners items={s.items || []} onChange={(items) => upd(sec.key, "items", items)} uploadImage={uploadImage} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const LEField = ({ label, value, onChange, area, testid }) => (
  <label className="block">
    <span className="block text-[12px] font-semibold text-gray-400 mb-1">{label}</span>
    {area
      ? <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={`${leInput} resize-y`} />
      : <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={leInput} />}
  </label>
);

const LESelect = ({ label, value, options, onChange, testid }) => (
  <label className="block">
    <span className="block text-[12px] font-semibold text-gray-400 mb-1">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={leInput}>
      {options.map((o) => <option key={o.v} value={o.v} style={{ color: "#111" }}>{o.l}</option>)}
    </select>
  </label>
);

const LEImage = ({ label, url, onUpload, onClear, testid }) => (
  <div>
    <span className="block text-[12px] font-semibold text-gray-400 mb-1">{label}</span>
    <div className="flex items-center gap-3">
      {url && <img src={`${LE_BASE}${url}`} alt="" className="h-14 w-20 object-cover rounded-lg border border-white/10" />}
      <label className="cursor-pointer inline-flex items-center gap-2 text-[13px] text-gray-200 border border-white/15 hover:border-white/40 px-3 py-2 rounded-lg">
        <Upload size={15} /> {url ? "Cambia" : "Carica"}
        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={testid}
          onChange={async (e) => { const f = e.target.files?.[0]; if (f) { try { await onUpload(f); toast.success("Immagine caricata"); } catch { toast.error("Upload non riuscito"); } } e.target.value = ""; }} />
      </label>
      {url && <button onClick={onClear} className="text-red-300 hover:text-red-200 text-[13px]"><Trash2 size={15} /></button>}
    </div>
  </div>
);

const LEPartners = ({ items, onChange, uploadImage }) => (
  <div>
    <span className="block text-[12px] font-semibold text-gray-400 mb-2">Partner (nome, logo, link)</span>
    <div className="space-y-2" data-testid="sa-landing-partners-list">
      {items.map((p, i) => (
        <div key={p._id || `partner-${i}`} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg p-2">
          {p.logo_url && <img src={`${LE_BASE}${p.logo_url}`} alt="" className="h-9 w-9 object-contain rounded" />}
          <input value={p.name || ""} placeholder="Nome" onChange={(e) => onChange(items.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className={`${leInput} flex-1`} data-testid={`sa-partner-name-${i}`} />
          <input value={p.url || ""} placeholder="https://" onChange={(e) => onChange(items.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} className={`${leInput} flex-1`} />
          <label className="cursor-pointer inline-flex items-center gap-1 text-[12px] text-gray-200 border border-white/15 px-2 py-2 rounded-lg">
            <Upload size={14} />
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={async (e) => { const f = e.target.files?.[0]; if (f) { try { const url = await uploadImage(f); onChange(items.map((x, j) => j === i ? { ...x, logo_url: url } : x)); toast.success("Logo caricato"); } catch { toast.error("Upload non riuscito"); } } e.target.value = ""; }} />
          </label>
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-red-300 hover:text-red-200 p-1"><Trash2 size={15} /></button>
        </div>
      ))}
    </div>
    <button onClick={() => onChange([...items, { _id: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())), name: "", logo_url: "", url: "" }])} data-testid="sa-partner-add"
      className="mt-2 inline-flex items-center gap-2 text-[13px] text-gray-200 border border-white/15 hover:border-white/40 px-3 py-2 rounded-lg">
      <Plus size={15} /> Aggiungi partner
    </button>
  </div>
);

const Loader = () => <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />;

const fmtSchedule = (dt) => {
  // 'YYYY-MM-DDTHH:MM' -> 'GG/MM/AAAA HH:MM'
  if (!dt || dt.length < 16) return dt || "";
  const [d, t] = dt.split("T");
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y} ${t}`;
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const fmtDuration = (secs) => {
  if (secs == null) return "—";
  const s = Math.max(0, Math.round(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
};

const MaintenanceEditor = () => {
  const [state, setState] = useState(null); // {active, message, eta, start_at, end_at, effective_active, scheduled_active}
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = () => saApi.get("/superadmin/maintenance/history").then((r) => setHistory(r.data.history || [])).catch(() => {});
  const refresh = () => saApi.get("/superadmin/maintenance").then((r) => setState(r.data)).catch((e) => console.error("Caricamento stato manutenzione non riuscito:", e));

  useEffect(() => {
    refresh();
    loadHistory();
    // Ricalcolo periodico: l'attivazione/disattivazione programmata cambia da sola.
    const iv = setInterval(() => { refresh(); loadHistory(); }, 30000);
    return () => clearInterval(iv);
  }, []);

  const put = async (next, okMsg) => {
    setSaving(true);
    try {
      const r = await saApi.put("/superadmin/maintenance", next);
      setState(r.data);
      toast.success(okMsg);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const toggle = () => put({ active: !state.active }, !state.active ? "Modalità manutenzione ATTIVATA" : "Modalità manutenzione disattivata");
  const save = () => put({ message: state.message, eta: state.eta, start_at: state.start_at || "", end_at: state.end_at || "", notice_days: state.notice_days }, "Impostazioni di manutenzione salvate");
  const clearSchedule = () => { setState((s) => ({ ...s, start_at: "", end_at: "" })); put({ start_at: "", end_at: "" }, "Programmazione rimossa"); };

  if (!state) return null;
  const active = !!state.active;
  const hasSchedule = !!(state.start_at && state.end_at);
  const noticeActive = !!state.notice_active;

  const badge = active
    ? { txt: "ATTIVA", cls: "bg-[#ff7e00]/20 text-[#ffb066]" }
    : hasSchedule
      ? { txt: "PREAVVISO PROGRAMMATO", cls: "bg-sky-500/20 text-sky-300" }
      : { txt: "Non attiva", cls: "bg-white/10 text-gray-400" };

  return (
    <div className="mb-10" data-testid="sa-maintenance-editor">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white text-[18px] font-black flex items-center gap-2"><Wrench size={18} className="text-[#ff7e00]" /> Modalità Manutenzione</h2>
        <span data-testid="sa-maintenance-status" className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${badge.cls}`}>{badge.txt}</span>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-white text-[14px] font-bold">Portale in manutenzione</div>
            <p className="text-[12px] text-gray-400 mt-0.5 max-w-[440px]">Se attiva, dipendenti e responsabili vedono una pagina di manutenzione. Admin HR e Superadmin accedono normalmente. Landing e login restano pubblici.</p>
          </div>
          <button
            onClick={toggle}
            disabled={saving}
            data-testid="sa-maintenance-toggle"
            role="switch"
            aria-checked={active}
            className={`relative shrink-0 w-14 h-8 rounded-full transition-colors disabled:opacity-60 ${active ? "bg-[#ff7e00]" : "bg-white/15"}`}
          >
            <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${active ? "translate-x-6" : "translate-x-0"}`} />
          </button>
        </div>

        {noticeActive && !active && (
          <div className="rounded-lg bg-sky-500/10 border border-sky-500/30 px-4 py-2.5 text-[12px] text-sky-300 font-semibold" data-testid="sa-maintenance-notice-note">
            Preavviso in corso: gli utenti loggati vedono l'avviso della manutenzione programmata ({fmtSchedule(state.start_at)} → {fmtSchedule(state.end_at)}). Ricorda di attivare il blocco col toggle all'orario previsto.
          </div>
        )}

        <label className="block">
          <span className="block text-[12px] font-semibold text-gray-400 mb-1">Messaggio mostrato agli utenti</span>
          <textarea rows={3} value={state.message || ""} onChange={(e) => setState((s) => ({ ...s, message: e.target.value }))} data-testid="sa-maintenance-message" className={`${leInput} resize-y`} placeholder="Es. Stiamo effettuando alcuni interventi di manutenzione…" />
        </label>
        <label className="block">
          <span className="block text-[12px] font-semibold text-gray-400 mb-1">Tempo stimato di ripristino (testo libero)</span>
          <input value={state.eta || ""} onChange={(e) => setState((s) => ({ ...s, eta: e.target.value }))} data-testid="sa-maintenance-eta" className={leInput} placeholder="Es. Previsto ripristino entro le ore 18:00 di oggi" />
        </label>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sky-300 text-[12px] font-black uppercase tracking-widest flex items-center gap-2"><Clock size={14} /> Preavviso manutenzione programmata (opzionale)</div>
            {hasSchedule && <button onClick={clearSchedule} disabled={saving} data-testid="sa-maintenance-clear-schedule" className="text-[11px] font-semibold text-gray-400 hover:text-white">Rimuovi</button>}
          </div>
          <p className="text-[11.5px] text-gray-500 mb-3">Imposta data e ora (ora italiana) della manutenzione: gli utenti loggati vedranno un banner di avviso nei giorni precedenti. <span className="text-gray-400">Il blocco NON si attiva da solo</span> — vai poi sul toggle qui sopra al momento previsto.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="block text-[12px] font-semibold text-gray-400 mb-1">Inizio</span>
              <input type="datetime-local" value={state.start_at || ""} onChange={(e) => setState((s) => ({ ...s, start_at: e.target.value }))} data-testid="sa-maintenance-start" className={`${leInput} [color-scheme:dark]`} />
            </label>
            <label className="block">
              <span className="block text-[12px] font-semibold text-gray-400 mb-1">Fine</span>
              <input type="datetime-local" value={state.end_at || ""} onChange={(e) => setState((s) => ({ ...s, end_at: e.target.value }))} data-testid="sa-maintenance-end" className={`${leInput} [color-scheme:dark]`} />
            </label>
            <label className="block">
              <span className="block text-[12px] font-semibold text-gray-400 mb-1">Giorni di preavviso</span>
              <input type="number" min={1} max={60} value={state.notice_days ?? 3} onChange={(e) => setState((s) => ({ ...s, notice_days: e.target.value }))} data-testid="sa-maintenance-notice-days" className={leInput} />
            </label>
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving} data-testid="sa-maintenance-save"
            className="flex items-center gap-2 bg-[#ff7e00] hover:bg-[#e56f00] disabled:opacity-60 text-white text-[13px] font-bold px-4 py-2 rounded-lg transition-colors">
            {saving ? <Loader /> : <Save size={16} />} Salva impostazioni
          </button>
        </div>

        {/* Storico manutenzioni */}
        <div className="border-t border-white/10 pt-4">
          <button onClick={() => { setShowHistory((v) => !v); if (!showHistory) loadHistory(); }} data-testid="sa-maintenance-history-toggle"
            className="w-full flex items-center justify-between text-left group">
            <span className="text-[#ffb066] text-[12px] font-black uppercase tracking-widest flex items-center gap-2"><History size={14} /> Storico manutenzioni</span>
            <span className="text-[12px] text-gray-400 group-hover:text-white flex items-center gap-1">
              {history.length > 0 ? `${history.length} registrate` : "nessuna"} {showHistory ? <ArrowUp size={13} /> : <ArrowDown size={13} />}
            </span>
          </button>
          {showHistory && (
            <div className="mt-3" data-testid="sa-maintenance-history">
              {history.length === 0 ? (
                <div className="text-[12px] text-gray-500 py-3" data-testid="sa-maintenance-history-empty">Nessuna manutenzione registrata finora.</div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-white/10">
                  <table className="w-full text-left text-[12px]">
                    <thead className="bg-white/5 text-gray-400 uppercase text-[10.5px] tracking-wide">
                      <tr>
                        <th className="px-3 py-2">Inizio</th>
                        <th className="px-3 py-2">Fine</th>
                        <th className="px-3 py-2">Durata</th>
                        <th className="px-3 py-2">Tipo</th>
                        <th className="px-3 py-2">Attivata da</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-200">
                      {history.map((h) => (
                        <tr key={h.id} className="border-t border-white/5" data-testid={`sa-maintenance-history-item-${h.id}`}>
                          <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(h.started_at)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {h.ongoing ? <span className="inline-flex items-center gap-1 text-[#ffb066] font-bold"><span className="w-1.5 h-1.5 rounded-full bg-[#ff7e00] animate-pulse" /> In corso</span> : fmtDateTime(h.ended_at)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap font-semibold">{h.ongoing ? "—" : fmtDuration(h.duration_seconds)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded-full ${h.source === "scheduled" ? "bg-sky-500/20 text-sky-300" : "bg-white/10 text-gray-300"}`}>
                              {h.source === "scheduled" ? "Programmata" : "Manuale"}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-400">{h.by || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LegalEditor = () => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    saApi.get("/superadmin/legal-content").then((r) => setContent(r.data.content)).catch((e) => console.error("Caricamento contenuti legali non riuscito:", e));
  }, []);

  const upd = (field, value) => setContent((c) => ({ ...c, [field]: value }));

  const save = async () => {
    setSaving(true);
    try { await saApi.put("/superadmin/legal-content", { content }); toast.success("Privacy e Cookie Policy aggiornate"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  if (!content) return null;

  return (
    <div className="mb-10" data-testid="sa-legal-editor">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white text-[18px] font-black flex items-center gap-2"><FileText size={18} className="text-[#ff7e00]" /> Privacy &amp; Cookie Policy</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setOpen((v) => !v)} className="text-gray-300 hover:text-white text-[13px] font-semibold border border-white/15 hover:border-white/40 px-3 py-2 rounded-lg transition-colors">
            {open ? "Comprimi" : "Espandi"}
          </button>
          <button onClick={save} disabled={saving} data-testid="sa-legal-save"
            className="flex items-center gap-2 bg-[#ff7e00] hover:bg-[#e56f00] disabled:opacity-60 text-white text-[13px] font-bold px-4 py-2 rounded-lg transition-colors">
            {saving ? <Loader /> : <Save size={16} />} Salva policy
          </button>
        </div>
      </div>
      {open && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-5">
          <div>
            <div className="text-[#ffb066] text-[12px] font-black uppercase tracking-widest mb-3">Titolare del trattamento</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <LEField label="Nome / Ragione sociale" value={content.controller_name} onChange={(v) => upd("controller_name", v)} testid="sa-legal-controller-name" />
              <LEField label="Email privacy" value={content.privacy_email} onChange={(v) => upd("privacy_email", v)} testid="sa-legal-email" />
              <LEField label="Sede / Indirizzo" value={content.controller_address} onChange={(v) => upd("controller_address", v)} testid="sa-legal-address" />
              <LEField label="Partita IVA / Codice Fiscale" value={content.controller_vat} onChange={(v) => upd("controller_vat", v)} testid="sa-legal-vat" />
            </div>
          </div>
          <div>
            <div className="text-[#ffb066] text-[12px] font-black uppercase tracking-widest mb-2">Testo Privacy Policy</div>
            <p className="text-[12px] text-gray-400 mb-2">Usa <code className="text-gray-300"># Titolo</code> per i titoli di sezione e <code className="text-gray-300">- voce</code> per gli elenchi puntati.</p>
            <textarea rows={12} value={content.privacy_body || ""} onChange={(e) => upd("privacy_body", e.target.value)} data-testid="sa-legal-privacy-body" className={`${leInput} resize-y font-mono text-[12.5px]`} />
          </div>
          <div>
            <div className="text-[#ffb066] text-[12px] font-black uppercase tracking-widest mb-2">Testo Cookie Policy</div>
            <textarea rows={12} value={content.cookie_body || ""} onChange={(e) => upd("cookie_body", e.target.value)} data-testid="sa-legal-cookie-body" className={`${leInput} resize-y font-mono text-[12.5px]`} />
          </div>
        </div>
      )}
    </div>
  );
};

const emptyPage = { title: "", slug: "", content: "", scope: "public", published: true, in_menu: false, parent_id: "", seo_title: "", meta_description: "", og_image: "", noindex: false };

const SITE_PAGES = [
  { key: "chisiamo", label: "Chi siamo" },
  { key: "lavora", label: "Lavora con noi" },
  { key: "partners", label: "Partners" },
  { key: "contatti", label: "Contatti" },
];

const SitePagesEditor = () => {
  const [sections, setSections] = useState(null);
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState({ title: "", body: "" });
  const [saving, setSaving] = useState(false);

  const load = () => saApi.get("/superadmin/landing-content").then((r) => setSections(r.data.sections || {})).catch((e) => { console.error("Caricamento contenuti sito non riuscito:", e); setSections({}); });
  useEffect(() => { load(); }, []);

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await saApi.post("/superadmin/pages/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caricamento immagine non riuscito");
      throw e;
    }
  };

  const openEdit = (key) => { const s = sections[key] || {}; setDraft({ title: s.title || "", body: s.body || "" }); setEditKey(key); };

  const save = async () => {
    setSaving(true);
    try {
      const next = { ...sections, [editKey]: { ...(sections[editKey] || {}), title: draft.title, body: draft.body } };
      await saApi.put("/superadmin/landing-content", { sections: next });
      toast.success("Pagina del sito aggiornata");
      setSections(next);
      setEditKey(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  if (!sections) return null;

  return (
    <div className="mb-10" data-testid="sa-site-pages-editor">
      <h3 className="text-white text-[15px] font-black flex items-center gap-2 mb-1"><Globe size={16} className="text-[#ff7e00]" /> Pagine del sito (menù di navigazione)</h3>
      <p className="text-[12px] text-gray-400 mb-4">Modifica testo e immagini delle sezioni della landing (Chi siamo, Lavora con noi, Partners, Contatti). Le modifiche compaiono subito sul sito pubblico.</p>
      <div className="bg-white/5 border border-white/10 rounded-2xl divide-y divide-white/5">
        {SITE_PAGES.map((sp) => (
          <div key={sp.key} className="flex items-center justify-between px-5 py-3.5" data-testid={`sa-site-page-${sp.key}`}>
            <div>
              <div className="text-white font-bold text-[14px]">{sp.label}</div>
              <div className="text-gray-500 text-[11px]">sezione landing</div>
            </div>
            <div className="flex items-center gap-2">
              <a href={`/#${sp.key}`} target="_blank" rel="noreferrer" data-testid={`sa-site-page-view-${sp.key}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10" title="Apri sul sito"><ExternalLink size={15} /></a>
              <button onClick={() => openEdit(sp.key)} data-testid={`sa-site-page-edit-${sp.key}`} className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-[12.5px] font-semibold px-3 py-1.5 rounded-lg transition-colors"><Pencil size={14} /> Modifica</button>
            </div>
          </div>
        ))}
      </div>

      {editKey && (
        <Modal title={`Modifica: ${SITE_PAGES.find((s) => s.key === editKey)?.label}`} testid="sa-site-page-modal" onClose={() => setEditKey(null)}>
          <Field label="Titolo sezione" value={draft.title} data-testid="sa-site-page-title" onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <label className="block mb-3">
            <span className="block text-[12px] font-semibold text-gray-600 mb-1">Contenuto</span>
            <RichTextEditor value={draft.body} onChange={(html) => setDraft((d) => ({ ...d, body: html }))} uploadImage={uploadImage} />
            <p className="text-[11px] text-gray-400 mt-1">Formatta il testo e inserisci immagini (a sinistra/centro/destra, con il testo che avvolge la foto).</p>
          </label>
          <button onClick={save} disabled={saving} data-testid="sa-site-page-save" className="w-full bg-[#16345c] hover:bg-[#0d1b2f] disabled:opacity-60 text-white font-bold py-2.5 rounded-lg text-[14px] flex items-center justify-center gap-2">{saving ? <Loader /> : <Save size={16} />} Salva e pubblica</button>
        </Modal>
      )}
    </div>
  );
};

const CustomPagesEditor = () => {
  const [pages, setPages] = useState(null);
  const [editing, setEditing] = useState(null); // page obj (with id) or new
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [normalizing, setNormalizing] = useState(false);

  const load = () => saApi.get("/superadmin/pages").then((r) => setPages(r.data.pages || [])).catch((e) => { console.error("Caricamento pagine non riuscito:", e); setPages([]); });
  useEffect(() => { load(); }, []);

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await saApi.post("/superadmin/pages/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caricamento immagine non riuscito");
      throw e;
    }
  };

  const onDrop = async (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); return; }
    const arr = [...pages];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(i, 0, moved);
    setPages(arr);
    setDragIdx(null);
    try {
      await saApi.post("/superadmin/pages/reorder", { ids: arr.map((p) => p.id) });
      toast.success("Ordine aggiornato");
    } catch (e) { toast.error("Errore nel riordino"); load(); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await saApi.put(`/superadmin/pages/${editing.id}`, editing);
      else await saApi.post("/superadmin/pages", editing);
      toast.success("Pagina salvata");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const normalizeHeadings = async () => {
    if (!window.confirm("Normalizzare i titoli di tutte le pagine con H1? (H1→H2, H2→H3, H3→H4 — modifica salvata a DB)")) return;
    setNormalizing(true);
    try {
      const r = await saApi.post("/superadmin/pages/normalize-headings");
      toast.success(r.data.updated ? `Titoli normalizzati in ${r.data.updated} pagine` : "Nessuna pagina da normalizzare");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nella normalizzazione"); }
    finally { setNormalizing(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare la pagina "${p.title}"?`)) return;
    try { await saApi.delete(`/superadmin/pages/${p.id}`); toast.success("Pagina eliminata"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  if (!pages) return null;

  return (
    <div className="mb-10" data-testid="sa-pages-editor">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white text-[18px] font-black flex items-center gap-2"><FileText size={18} className="text-[#ff7e00]" /> Pagine personalizzate</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setOpen((v) => !v)} className="text-gray-300 hover:text-white text-[13px] font-semibold border border-white/15 hover:border-white/40 px-3 py-2 rounded-lg transition-colors">{open ? "Comprimi" : "Espandi"}</button>
          <button onClick={normalizeHeadings} disabled={normalizing} data-testid="sa-pages-normalize-headings" title="Riscrive a DB i titoli delle pagine con H1: H1→H2, H2→H3, H3→H4" className="flex items-center gap-2 text-gray-300 hover:text-white text-[13px] font-semibold border border-white/15 hover:border-white/40 disabled:opacity-60 px-3 py-2 rounded-lg transition-colors">{normalizing ? <Loader /> : <Heading size={15} />} Normalizza titoli</button>
          <button onClick={() => setEditing({ ...emptyPage })} data-testid="sa-pages-new" className="flex items-center gap-2 bg-[#ff7e00] hover:bg-[#e56f00] text-white text-[13px] font-bold px-4 py-2 rounded-lg transition-colors"><Plus size={16} /> Nuova pagina</button>
        </div>
      </div>
      {open && (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          {pages.length === 0 ? (
            <div className="px-5 py-6 text-[13px] text-gray-400" data-testid="sa-pages-empty">Nessuna pagina creata. Usa "Nuova pagina" per aggiungerne una.</div>
          ) : (
            <table className="w-full text-left text-[13px]">
              <thead className="bg-white/5 text-gray-400 text-[11px] uppercase tracking-wide">
                <tr><th className="px-4 py-3">Pagina <span className="normal-case text-gray-500 font-normal">· trascina per ordinare</span></th><th className="px-3 py-3">Ambito</th><th className="px-3 py-3">Stato</th><th className="px-3 py-3">Menù</th><th className="px-3 py-3 text-right">Azioni</th></tr>
              </thead>
              <tbody>
                {pages.map((p, i) => (
                  <tr key={p.id} className={`border-t border-white/5 ${dragIdx === i ? "opacity-40" : ""}`} data-testid={`sa-page-row-${p.id}`}
                    draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)} onDragEnd={() => setDragIdx(null)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <GripVertical size={15} className="text-gray-500 cursor-grab shrink-0" data-testid={`sa-page-drag-${p.id}`} />
                        <div>
                          <div className="text-white font-bold">{p.title}</div>
                          <div className="text-gray-500 text-[11px]">/p/{p.slug}{p.parent_id ? <span className="text-sky-300"> · ↳ sottopagina di {pages.find((x) => x.id === p.parent_id)?.title || "?"}</span> : null}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full ${p.scope === "internal" ? "bg-sky-500/20 text-sky-300" : "bg-emerald-500/20 text-emerald-300"}`}>
                        {p.scope === "internal" ? <><Lock size={11} /> Interna</> : <><Globe size={11} /> Pubblica</>}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded-full ${p.published ? "bg-[#ff7e00]/20 text-[#ffb066]" : "bg-white/10 text-gray-400"}`}>{p.published ? "Pubblicata" : "Bozza"}</span>
                    </td>
                    <td className="px-3 py-3 text-gray-300">{p.in_menu ? "Sì" : "—"}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" title="Apri" data-testid={`sa-page-view-${p.id}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10"><ExternalLink size={15} /></a>
                        <button title="Modifica" onClick={() => setEditing({ ...p })} data-testid={`sa-page-edit-${p.id}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10"><Pencil size={15} /></button>
                        <button title="Elimina" onClick={() => remove(p)} data-testid={`sa-page-delete-${p.id}`} className="p-1.5 rounded-lg text-red-300 hover:text-red-200 hover:bg-red-500/10"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {editing && (
        <Modal title={editing.id ? "Modifica pagina" : "Nuova pagina"} testid="sa-page-modal" onClose={() => setEditing(null)}>
          <Field label="Titolo" value={editing.title} data-testid="sa-page-title" onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Es. Domande frequenti" />
          <Field label="Slug (URL: /p/…)" value={editing.slug} data-testid="sa-page-slug" onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="lascia vuoto per generarlo dal titolo" />
          <label className="block mb-3">
            <span className="block text-[12px] font-semibold text-gray-600 mb-1">Ambito</span>
            <select value={editing.scope} data-testid="sa-page-scope" onChange={(e) => setEditing({ ...editing, scope: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[14px] outline-none focus:border-[#16345c]">
              <option value="public">Pubblica (sito, tutti)</option>
              <option value="internal">Interna (solo utenti loggati)</option>
            </select>
          </label>
          {(() => {
            const hasChildren = editing.id && pages.some((x) => x.parent_id === editing.id);
            const parentOptions = pages.filter((x) => !x.parent_id && x.id !== editing.id);
            return (
              <label className="block mb-3">
                <span className="block text-[12px] font-semibold text-gray-600 mb-1">Pagina superiore (genitore)</span>
                <select value={editing.parent_id || ""} data-testid="sa-page-parent" disabled={hasChildren}
                  onChange={(e) => setEditing({ ...editing, parent_id: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[14px] outline-none focus:border-[#16345c] disabled:bg-gray-100 disabled:text-gray-400">
                  <option value="">— Nessuna (pagina principale)</option>
                  {parentOptions.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                </select>
                <span className="block text-[11px] text-gray-400 mt-1">
                  {hasChildren ? "Questa pagina ha già delle sottopagine, quindi non può diventare figlia di un'altra." : "Se selezioni un genitore, questa pagina apparirà nel suo menù a tendina."}
                </span>
              </label>
            );
          })()}
          <label className="block mb-3">
            <span className="block text-[12px] font-semibold text-gray-600 mb-1">Contenuto</span>
            <RichTextEditor
              value={editing.content}
              onChange={(html) => setEditing((prev) => ({ ...prev, content: html }))}
              uploadImage={uploadImage}
            />
            <p className="text-[11px] text-gray-400 mt-1">Usa i pulsanti per formattare. Per le immagini scegli l'allineamento (sinistra/centro/destra): il testo avvolge la foto senza spazi vuoti.</p>
          </label>
          <PageSeoFields page={editing} onChange={(patch) => setEditing((prev) => ({ ...prev, ...patch }))} />
          <div className="flex items-center gap-6 mb-4">
            <label className="flex items-center gap-2 text-[13px] font-semibold text-gray-700 cursor-pointer">
              <input type="checkbox" checked={editing.published} data-testid="sa-page-published" onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="w-4 h-4 accent-[#ff7e00]" /> Pubblicata
            </label>
            <label className="flex items-center gap-2 text-[13px] font-semibold text-gray-700 cursor-pointer">
              <input type="checkbox" checked={editing.in_menu} data-testid="sa-page-inmenu" onChange={(e) => setEditing({ ...editing, in_menu: e.target.checked })} className="w-4 h-4 accent-[#ff7e00]" /> Mostra nel menù
            </label>
          </div>
          <button onClick={save} disabled={saving} data-testid="sa-page-save" className="w-full bg-[#16345c] hover:bg-[#0d1b2f] disabled:opacity-60 text-white font-bold py-2.5 rounded-lg text-[14px] flex items-center justify-center gap-2">{saving ? <Loader /> : <Save size={16} />} Salva pagina</button>
        </Modal>
      )}
    </div>
  );
};


export default SuperadminDashboard;
